import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, CardBody, Button } from 'reactstrap';
import parse from 'html-react-parser';
import cloneDeep from 'lodash.clonedeep';
import ReactDevicePreview from 'react-device-preview';

import Loader from '../../shared/loader/loader';
import BackButton from '../../components/Common/BackButton';
import PopupAddStudyGuide from './PopupAddStudyGuide';
import PopupConfirmation from '../../components/PopupConfirmation';
import { ShowNotification, GetQueryParam, SetStatusStyle } from '../../helpers/general/generalHelpers';
import { getDetailStudyGuide, putUpdateStudyGuide } from '../../service/studyGuide';
import MobileScreen from './MobileScreen';
import { Buffer } from "buffer";

// 1. Import your new custom Quill editor.
import CustomQuillEditor from '../../components/CustomQuillEditor/CustomQuillEditor';

// 2. We only need `uploadHtmlAndGetContent` for saving, as the editor handles its own uploads.
import { uploadHtmlAndGetContent } from '../../components/Utils';

const EditStudyGuide = (props) => {
  const studyGuideId = GetQueryParam(props.location.search, 'id');
  const [contentHtml, setContentHtml] = useState('');
  const [pages, setPages] = useState([]);
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailStudyGuide, setDetailStudyGuide] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [placeholder, setPlaceholder] = useState('');

  // 3. This ref is now crucial for interacting with your custom editor.
  const quillRef = useRef(null);

  Buffer.from("anything", "base64");
  window.Buffer = window.Buffer || require("buffer").Buffer;


  // Add this helper function inside your EditStudyGuide component

  /**
   * Processes a raw string from the API.
   * If it's a placeholder like "Page 1", it separates it.
   * Otherwise, it prepares it as content for the editor.
   * @param {string} rawString - The string from the `content` array.
   * @returns {{contentForEditor: string, placeholderText: string}}
   */
  const processPageString = (rawString) => {
    // Regex to check if the entire string is "Page" followed by number(s).
    // ^ matches the start of the string, $ matches the end.
    const placeholderRegex = /^Page\s*\d+$/i;

    if (rawString && rawString.match(placeholderRegex)) {
      // The string IS a placeholder.
      return {
        contentForEditor: '<p><br></p>', // Use a blank line for the editor
        placeholderText: rawString,      // Use the original string as the placeholder
      };
    }

    // The string is actual content.
    // It might be plain text or saved HTML. We'll treat it as HTML.
    // If it's empty/null, default to a blank line.
    return {
      contentForEditor: rawString || '<p><br></p>',
      placeholderText: '', // No placeholder
    };
  };

  const handleUpdateDetailStudyGuide = () => {
    getDetailStudyGuide({ id: studyGuideId })
      .then((response) => {
        const data = response.data.studyGuide;
        setDetailStudyGuide((prev) => {
          return {
            ...prev,
            order: data.order,
            title: data.title,
            studyLevelId: data.studyLevelId,
            chapter: data.chapter,
            active: data.active,
            subject: data.subject
          };
        });
        ShowNotification({
          type: 'success',
          message: 'Great, you’ve successfully update study guide!'
        });
      })
      .catch((error) => {
        ShowNotification({
          type: 'error',
          message: 'Oops, sorry! Something went wrong, please try again.'
        });
      });
  };

  const handleSaveStudyQuidesPages = async (isAddingPage = false) => {
    console.log(isAddingPage);
    if (!quillRef.current) {
      console.error("Editor is not yet available.");
      ShowNotification({
        type: 'error',
        message: 'Editor is not ready, please wait a moment and try again.'
      });
      return;
    }

    const updatedPages = cloneDeep(pages);
    const currPageIndex = currentPage - 1;

    let currentEditorContent;
    try {
      currentEditorContent = quillRef.current.root.innerHTML;
    } catch (error) {
      ShowNotification({ type: 'error', message: 'Failed to get editor content.' });
      return;
    }


    let bodyHtml;
    try {
      bodyHtml = await uploadHtmlAndGetContent(currentEditorContent);
      console.log("HTML processed and uploaded. Result:", bodyHtml);
    } catch (error) {
      console.error("Error during uploadHtmlAndGetContent:", error);
      ShowNotification({ type: 'error', message: 'Failed to process editor content.' });
      return;
    }


    // --- Step 2: ALWAYS update the current page's content in our temporary array ---
    updatedPages[currPageIndex] = bodyHtml;
    console.log("Updated current page content in updatedPages.");


    // --- Step 3: CONDITIONALLY add a new page if requested ---
    if (isAddingPage) {
      // Insert the new blank page *after* the current page
      // If currentPage is 3, it inserts at index 3 (after the 3rd page, which is at index 2)
      updatedPages.splice(currentPage, 0, `<p></p>`);
      console.log("New page added to updatedPages. New length:", updatedPages.length);
    }

    // --- Step 4: Save to the API if content has changed OR if a page was added ---
    const contentChanged = pages[currPageIndex] !== updatedPages[currPageIndex];
    const lengthChanged = updatedPages.length !== pages.length;

    console.log("Checking save condition:", {
      contentChanged,
      lengthChanged,
      currentPagesLength: pages.length,
      updatedPagesLength: updatedPages.length,
      initialPageContent: pages[currPageIndex],
      newCurrentPageContent: updatedPages[currPageIndex]
    });

    if (contentChanged || lengthChanged) {
      console.log("Calling putUpdateStudyGuide with:", { id: studyGuideId, body: { content: updatedPages } });
      await putUpdateStudyGuide({ id: studyGuideId, body: { content: updatedPages } })
        .then((response) => {
          console.log("API Success Response:", response);
          // Update the local state with the confirmed state from the server
          setPages(response.data.studyguide.content);
          const successMessage = isAddingPage
            ? `Page ${currentPage} saved & new page added!`
            : `Great, you’ve successfully saved page ${currentPage}`;
          ShowNotification({
            type: 'success',
            message: successMessage
          });
        })
        .catch((error) => {
          // THIS IS THE MOST IMPORTANT CHANGE: Log the actual error object
          console.error("API Error during putUpdateStudyGuide:", error);
          ShowNotification({
            type: 'error',
            // Consider showing more specific error message if error.response exists
            message: error.response?.data?.message || 'Oops, sorry! Something went wrong, please try again.'
          });
        });
    } else {
      console.info(`No changes on page ${currentPage} and no new page added.`);
    }
  };

  const handleGetDetailStudyGuide = () => {
    setLoader(true);
    getDetailStudyGuide({ id: studyGuideId })
      .then((response) => {
        const studyGuideData = response.data.studyGuide;
        const allPages = studyGuideData.content;
        const firstPageString = allPages[0] || '';

        // Use the new helper to process the string
        const { contentForEditor, placeholderText } = processPageString(firstPageString);

        setDetailStudyGuide(studyGuideData);
        setPages(allPages);                 // Store the original array from the API
        setContentHtml(contentForEditor);   // Set the processed content for the editor
        setPlaceholder(placeholderText);    // Set the placeholder text
      })
      .catch((error) => {
        ShowNotification({
          type: 'error',
          message: error.message,
        });
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const toggleModal = () => {
    setIsPopupOpen(!isPopupOpen);
    document.body.classList.add('no_padding');
  };

  const handleSaveButton = () => {
    handleSaveStudyQuidesPages();
  };

  const handleCancelChanges = () => {
    // Revert the state to the original content for the current page
    setContentHtml(pages[currentPage - 1] || '');
    setIsCancelOpen(false);
    ShowNotification({
      type: 'success',
      message: `Great, you’ve successfully canceled changes on page ${currentPage}`
    });
  };

  const handleDeletePage = () => {
    const pagesContent = cloneDeep(pages);
    const currPages = currentPage - 1;
    if (currPages > -1) {
      pagesContent.splice(currPages, 1);
    }
    putUpdateStudyGuide({ id: studyGuideId, body: { content: pagesContent } })
      .then((response) => {
        const tempPages = response.data.studyguide.content;
        setIsDeleteOpen(false);
        setPages(tempPages);
        const newCurrentPage = currentPage < 2 ? 1 : currentPage - 1;
        setCurrentPage(newCurrentPage);
        setContentHtml(tempPages[newCurrentPage - 1] || '');
        ShowNotification({
          type: 'success',
          message: `Great, you’ve successfully deleted page ${currentPage}`
        });
      })
      .catch((error) => {
        ShowNotification({
          type: 'error',
          message: 'Oops, sorry! Something went wrong, please try again.'
        });
      });
  };

  const ChangePage = async (type) => {
    switch (type) {
      case 'next':
        await handleSaveStudyQuidesPages(false);
        setCurrentPage((prev) => prev + 1);
        break;
      case 'prev':
        await handleSaveStudyQuidesPages(false);
        setCurrentPage((prev) => prev - 1);
        break;
      case 'add':
        await handleSaveStudyQuidesPages(true);
        setCurrentPage((prev) => prev + 1);
        break;
      default:
    }
  };

  // 4. This handler is now different. It gets the latest HTML from the Quill instance via the ref.
  const handleEditorChange = () => {
    if (quillRef.current) {
      const newHtml = quillRef.current.root.innerHTML;
      if (contentHtml !== newHtml) {
        setContentHtml(newHtml);
      }
    }
  };

  useEffect(() => {
    handleGetDetailStudyGuide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newPageString = pages[currentPage - 1];
    if (newPageString !== undefined) {
      // Use the helper to process the string for the current page
      const { contentForEditor, placeholderText } = processPageString(newPageString);

      setPlaceholder(placeholderText);
      setContentHtml(contentForEditor);

      // Imperatively update the Quill editor's content
      if (quillRef.current) {
        const editorContent = quillRef.current.root.innerHTML;
        if (editorContent !== contentForEditor) {
          quillRef.current.clipboard.dangerouslyPasteHTML(contentForEditor);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pages]);

  return (
    <React.Fragment>
      <Loader loading={loader} />
      <div className="page-content">
        <Container fluid className="relative">
          <BackButton text="Back to Study Guides" url="/studyguides" />

          <Card>
            <CardBody>
              <Row>
                <Col md="3" className="justify-content-center">
                  <h4 className="card-title mb-3">{detailStudyGuide?.title}</h4>
                </Col>
                <Col className="justify-content-left">
                  <SetStatusStyle status={detailStudyGuide?.active ? 'active' : 'inactive'} />
                </Col>
                <Col className="col text-md-right text-sm-right">
                  <i onClick={() => setIsPopupOpen(true)} className="bx bxs-edit-alt font-size-16 btn p-0"></i>
                </Col>
              </Row>
              <Row>
                <Col md="3" className="justify-content-center">
                  {detailStudyGuide?.subject?.title} - {detailStudyGuide?.studyLevelId}
                  <br />
                  {detailStudyGuide?.chapter?.title}
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Row className="agora-lato">
                <Col md="1">
                  <Button
                    type="button"
                    disabled={currentPage === 1}
                    className="btn button-arrow btn-primary left-right-button waves-effect"
                    style={{ fontSize: '25px' }}
                    onClick={(e) => {
                      ChangePage('prev');
                    }}
                  >
                    <i className="bx bx-left-arrow"></i>
                  </Button>
                </Col>
                <Col md="4" className="text-center">
                  <ReactDevicePreview device="iphone8" scale="0.7">
                    <div className="device-content">
                      <MobileScreen
                        cardHeaderText={detailStudyGuide?.title}
                        cardSubHeaderText={`${detailStudyGuide?.subject?.title} - ${detailStudyGuide?.studyLevelId}`}
                        cardSubHeaderText2={detailStudyGuide?.chapter?.title}
                        currentPage={currentPage}
                        pagesLength={pages.length}
                      >
                        {parse(contentHtml || '')}
                      </MobileScreen>
                    </div>
                  </ReactDevicePreview>
                </Col>
                <Col md="1">
                  <div style={{ display: 'flex' }}>
                    {currentPage !== pages.length && (
                      <Button
                        type="button"
                        className="btn button-arrow btn-primary left-right-button waves-effect agora-dark-grey-color"
                        style={{ fontSize: '25px' }}
                        onClick={(e) => {
                          ChangePage('next');
                        }}
                      >
                        <i className="bx bx-right-arrow"></i>
                      </Button>
                    )}
                    <Button
                      type="button"
                      className="btn button-arrow btn-primary left-right-button waves-effect agora-dark-grey-color"
                      style={{ fontSize: '25px' }}
                      onClick={(e) => {
                        ChangePage('add');
                      }}
                    >
                      <i className="bx bx-plus"></i>
                    </Button>
                  </div>
                </Col>
                <Col md="6">
                  {/* --- 6. The new editor integration is much simpler. --- */}
                  <CustomQuillEditor
                    ref={quillRef}
                    initialHtml={contentHtml}
                    onTextChange={handleEditorChange}
                    placeholder={placeholder}
                  />

                  <div className="icon-buttons mt-2 text-center">
                    <Button
                      type="button"
                      color="link"
                      disabled={pages.length === 1}
                      className="btn btn-link waves-effect text-danger"
                      onClick={(e) => {
                        setIsDeleteOpen(true);
                      }}
                    >
                      <i className="bx bxs-trash-alt"></i> Delete Page
                    </Button>
                  </div>
                  <Row style={{ marginTop: '80px', float: 'right' }}>
                    <Col className="col-sm-12 col-md-auto mb-4" style={{ paddingTop: '27px' }}>
                      <Button
                        type="button"
                        onClick={() => setIsCancelOpen(true)}
                        className="btn btn-primary agora-button cancel-button border-radius waves-effect waves-light ml-2"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        onClick={handleSaveButton}
                        className="btn btn-primary agora-button save-button border-radius waves-effect waves-light"
                      >
                        Save
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>
      <PopupAddStudyGuide
        edit={detailStudyGuide}
        show={isPopupOpen}
        UpdateContent={handleUpdateDetailStudyGuide}
        toggleModal={toggleModal}
      />

      <PopupConfirmation
        show={isDeleteOpen}
        actionButtonN={() => setIsDeleteOpen(false)}
        actionButtonY={handleDeletePage}
        title={'Wait a minute…'}
        body={'Are you sure want to remove this page from study guide content?'}
      />

      <PopupConfirmation
        show={isCancelOpen}
        actionButtonN={() => setIsCancelOpen(false)}
        actionButtonY={handleCancelChanges}
        title={'Wait a minute…'}
        body={'Are you sure want to cancel changes content on this pages?'}
      />
    </React.Fragment>
  );
};

export default EditStudyGuide;